import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Wrench, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { categoryImage } from "../../lib/storefront";
import { API } from "../../lib/api";
import { useLocationCtx } from "../../context/LocationContext";

const IMG_BASE = API.replace(/\/api$/, "");

function lineTarget(line) {
  if (line.category) return `/services?category=${encodeURIComponent(line.category)}`;
  return "/services";
}

// Available services driven by the header location selector
export function AvailableServices() {
  const { loc } = useLocationCtx();
  const navigate = useNavigate();
  if (!loc) return null;
  const services = loc.services || [];

  return (
    <section className="bg-white border-y border-[#E2E8F0] py-14 md:py-16" data-testid="available-services-section">
      <div className="max-w-5xl mx-auto px-5">
        {!loc.serviceable ? (
          <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-2xl p-6 flex gap-4" data-testid="home-not-serviceable">
            <WarningCircle size={28} weight="fill" className="text-[#EA580C] shrink-0" />
            <div>
              <h3 className="font-[Outfit] font-bold text-lg text-[#9A3412]">We're not in your area yet</h3>
              <p className="text-sm text-[#7C2D12] mt-1">Fixigo isn't serving pincode <b>{loc.pincode}</b> right now. We're expanding fast — leave us a note and we'll notify you the moment we launch nearby.</p>
              <a href={`mailto:support@fixigohomeservices.in?subject=Notify me — pincode ${loc.pincode}`} className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-[#4F46E5] hover:underline" data-testid="home-notify-me">Notify me <ArrowRight size={14} weight="bold" /></a>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 justify-center">
              <span className="inline-flex items-center gap-2 bg-[#166534]/10 text-[#166534] font-semibold text-sm px-4 py-2 rounded-full">
                <CheckCircle size={16} weight="fill" /> Available in {loc.locality ? `${loc.locality}, ` : ""}{loc.city}
              </span>
            </div>
            <h2 className="font-[Outfit] text-3xl md:text-4xl font-bold tracking-tight text-center mt-4">Services available near you</h2>
            {services.length === 0 ? (
              <p className="text-[#475569] text-center mt-4">No services are active for this pincode yet.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {services.map((s) => (
                  <button key={s.slug} onClick={() => navigate(lineTarget(s))} data-testid={`service-line-${s.slug}`}
                    className="group relative aspect-square rounded-2xl overflow-hidden text-left shadow-sm hover:shadow-xl transition-all duration-300">
                    <img src={categoryImage(s.category || s.label)} alt={s.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <div className="flex items-center gap-1.5 text-white">
                        <Wrench size={14} weight="fill" className="text-[#C7D2FE]" />
                        <h3 className="font-[Outfit] font-bold text-sm leading-tight">{s.label}</h3>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export function PopularCities() {
  const [cities, setCities] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.get("/public/cities").then((r) => setCities(r.data)).catch(() => {}); }, []);
  if (cities.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-5 py-16 md:py-20" data-testid="popular-cities-section">
      <div className="text-center">
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5]">Now serving</p>
        <h2 className="font-[Outfit] text-3xl md:text-4xl font-bold tracking-tight mt-2">Popular Cities</h2>
        <p className="text-[#475569] mt-2">Explore Fixigo services available in your city.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-10">
        {cities.map((c) => (
          <button key={c.id} onClick={() => navigate(`/city/${c.city_slug}`)} data-testid={`popular-city-${c.city_slug}`}
            className="group relative aspect-[4/5] rounded-3xl overflow-hidden text-left shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
            {c.city_image_url
              ? <img src={`${IMG_BASE}${c.city_image_url}`} alt={c.city_name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              : <div className="absolute inset-0 bg-gradient-to-br from-[#4F46E5] to-[#1E293B]" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="flex items-center gap-1.5 text-white/80 text-xs"><MapPin size={12} weight="fill" /> {c.state_name}</div>
              <h3 className="font-[Outfit] font-extrabold text-xl text-white mt-1">{c.city_name}</h3>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-[#C7D2FE] mt-2 group-hover:gap-2 transition-all duration-200">
                Explore Services <ArrowRight size={14} weight="bold" />
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
